import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApplicationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const appId = searchParams.get('app_id');
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/application/checkout/status/${sessionId}`);
        setStatus(response.data);
      } catch (err) {
        setError('Unable to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white/70">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  const isPaymentComplete = status?.payment_status === 'paid' || status?.status === 'complete';

  return (
    <div data-testid="application-success-page">
      <SEO 
        title="Application Submitted | Queen B Logistics"
        description="Your application has been submitted successfully. Welcome to the Queen B Logistics ecosystem."
      />
      
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-black text-white uppercase">
            Application<br />
            <span className="text-[#D4AF37]">{isPaymentComplete ? 'Submitted' : 'Received'}</span>
          </h1>
        </div>
      </section>

      {/* Success Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          {isPaymentComplete ? (
            <>
              <CheckCircle className="w-24 h-24 text-[#D4AF37] mx-auto mb-8" />
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-4">
                Payment Confirmed!
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Thank you for your payment. Your application is now under review by our team.
                We will send you an email with next steps within 24-48 hours.
              </p>
            </>
          ) : (
            <>
              <AlertCircle className="w-24 h-24 text-yellow-500 mx-auto mb-8" />
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-4">
                Application Received
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Your application has been received but payment is still pending.
                If you did not complete payment, our team will reach out with payment instructions.
              </p>
            </>
          )}

          {status && (
            <div className="bg-[#121212] border border-white/10 p-6 mb-8 text-left">
              <h3 className="text-white font-bold mb-4">Payment Details:</h3>
              <div className="space-y-2 text-white/70">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={isPaymentComplete ? 'text-green-500' : 'text-yellow-500'}>
                    {isPaymentComplete ? 'Paid' : 'Pending'}
                  </span>
                </div>
                {status.amount_total > 0 && (
                  <div className="flex justify-between">
                    <span>Amount:</span>
                    <span className="text-[#D4AF37]">
                      ${status.amount_total.toFixed(2)} {status.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* What Happens Next */}
          <div className="bg-[#121212] border border-white/10 p-8 text-left mb-8">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#D4AF37] mb-4">
              What Happens Next?
            </h3>
            <ol className="space-y-4 text-white/70">
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] font-bold">1.</span>
                <div>
                  <span className="text-white font-medium">Review</span>
                  <p className="text-sm">We review your authority status and service selection within 24-48 hours.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] font-bold">2.</span>
                <div>
                  <span className="text-white font-medium">Approval + Welcome Email</span>
                  <p className="text-sm">You receive approval confirmation and Hive portal access details.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] font-bold">3.</span>
                <div>
                  <span className="text-white font-medium">Onboarding Activation</span>
                  <p className="text-sm">
                    - Build clients: Authority roadmap + timers begin<br />
                    - Protect clients: Compliance calendar activated<br />
                    - Grow clients: Dispatch setup call scheduled
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D4AF37] font-bold">4.</span>
                <div>
                  <span className="text-white font-medium">Operations Begin</span>
                  <p className="text-sm">Your system goes live under the Build - Protect - Grow framework.</p>
                </div>
              </li>
            </ol>
          </div>

          <Link to="/" className="btn-primary inline-block" data-testid="return-home-btn">
            Return Home
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ApplicationSuccessPage;
